import { parseTemplateSchema, type TemplateFieldSpec } from './template-schema';

export interface SwitchReport {
  carriedOver: string[];
  parked: string[];
  missing: string[];
}

export interface SwitchTemplateOptions {
  customData: Record<string, unknown> | null | undefined;
  fromTemplateId: string | null | undefined;
  toTemplateId: string;
  fromSchemaRaw?: unknown;
  toSchemaRaw?: unknown;
  fromSchema?: TemplateFieldSpec[] | null;
  toSchema?: TemplateFieldSpec[] | null;
  occasionType?: string;
  isAiNative?: boolean;
  /** Explicit style for the TARGET template, e.g. from EXPERIENCE_STYLE_BY_TEMPLATE_ID.
   *  When omitted, the previous experience.template (if any) is carried forward
   *  rather than dropped — see deriveExperience(). */
  style?: string;
}

export interface SwitchTemplateResult {
  customData: Record<string, unknown>;
  report: SwitchReport;
}

/** Helper to get deep value by dot-path from an object. */
function getByPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return current;
}

/** Helper to set deep value by dot-path in an object (mutates target). */
function setByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!current[part] || typeof current[part] !== 'object' || Array.isArray(current[part])) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

/** Helper to delete deep value by dot-path in an object (mutates target, cleans empty parents). */
function deleteByPath(obj: Record<string, unknown>, path: string): boolean {
  const parts = path.split('.');
  function helper(curr: Record<string, unknown>, index: number): boolean {
    const key = parts[index];
    if (index === parts.length - 1) {
      if (key in curr) {
        delete curr[key];
        return true;
      }
      return false;
    }
    const next = curr[key];
    if (next && typeof next === 'object' && !Array.isArray(next)) {
      const deleted = helper(next as Record<string, unknown>, index + 1);
      if (deleted && Object.keys(next as object).length === 0) {
        delete curr[key];
      }
      return deleted;
    }
    return false;
  }
  return helper(obj, 0);
}

/**
 * Derive the `experience` metadata block for wizard and rendering pipelines.
 * Recomputed on every template switch, never parked or merged.
 *
 * `style` (e.g. 'royal' | 'minimal' | 'heritage') is resolved by the caller
 * from a per-template lookup that has no generic rule (see
 * EXPERIENCE_STYLE_BY_TEMPLATE_ID in StepTemplate.tsx) — it is NOT derivable
 * from templateId/occasionType alone, so it's threaded through rather than
 * computed here.
 */
export function deriveExperience(
  templateId: string,
  occasionType?: string,
  isAiNative?: boolean,
  style?: string,
): Record<string, unknown> {
  const aiNative = isAiNative ?? templateId.startsWith('exp-cinematic-');
  return {
    templateId,
    occasionType: occasionType || 'WEDDING',
    aiNative,
    ...(style ? { template: style } : {}),
  };
}

/**
 * Pure function implementing V2 template switch semantics.
 */
export function switchTemplate(options: SwitchTemplateOptions): SwitchTemplateResult {
  const {
    customData: rawCustomData,
    fromTemplateId,
    toTemplateId,
    fromSchemaRaw,
    toSchemaRaw,
    occasionType,
    isAiNative,
    style,
  } = options;

  const fromSchema = options.fromSchema ?? parseTemplateSchema(fromSchemaRaw);
  const toSchema = options.toSchema ?? parseTemplateSchema(toSchemaRaw);

  const report: SwitchReport = {
    carriedOver: [],
    parked: [],
    missing: [],
  };

  // Deep clone customData to avoid mutating input
  const nextCustomData: Record<string, unknown> = rawCustomData
    ? JSON.parse(JSON.stringify(rawCustomData))
    : {};

  // Carry the previous style forward when the caller doesn't resolve a new
  // one — never silently reset it to the deriveExperience() default.
  const previousExperience = rawCustomData?.experience as Record<string, unknown> | undefined;
  const resolvedStyle = style ?? (previousExperience?.template as string | undefined);

  // MERGE, never replace. For AI-native templates, `experience` isn't just
  // {templateId, occasionType, aiNative} — it's the actual Experience DSL
  // (`meta`: names/dates/tagline, `rituals`, `version`, `plan`, built by
  // ExperienceDetailsStep / buildExperienceDsl). Overwriting the whole object
  // with deriveExperience()'s output would silently delete the user's typed
  // DSL content on every switch. Only the derived/recomputed keys
  // (templateId, occasionType, aiNative, template/style) are meant to change;
  // everything else the previous experience held must survive untouched.
  nextCustomData.experience = {
    ...previousExperience,
    ...deriveExperience(toTemplateId, occasionType, isAiNative, resolvedStyle),
  };

  // Extract _parked storage if present
  const parkedStore = (nextCustomData._parked as Record<string, Record<string, unknown>>) || {};
  delete nextCustomData._parked;

  // Step 1: Restore any previously parked fields the target template wants —
  // search EVERY parked bucket, not just parkedStore[toTemplateId]. A field
  // can get orphaned into a DIFFERENT bucket during a multi-hop switch chain
  // (A -> B -> C -> A): if A and B both declare a field, and it gets parked
  // while leaving B for C, it lands in parkedStore['B'] — not parkedStore['A']
  // — even though switching back to A is what should restore it. Searching
  // every bucket by field key (rather than trusting which template happened
  // to be "leaving" when it got parked) restores it wherever it actually is.
  if (toSchema) {
    for (const field of toSchema) {
      if (getByPath(nextCustomData, field.key) !== undefined) continue;
      for (const bucketId of Object.keys(parkedStore)) {
        const bucket = parkedStore[bucketId];
        if (field.key in bucket) {
          setByPath(nextCustomData, field.key, bucket[field.key]);
          delete bucket[field.key];
          if (Object.keys(bucket).length === 0) delete parkedStore[bucketId];
          break;
        }
      }
    }
  }

  // Step 2: Evaluate fields under fromSchema and toSchema
  // If either schema is null ("supports everything"), no fields are parked.
  if (fromSchema && toSchema && fromTemplateId) {
    const newlyParked: Record<string, unknown> = {};

    for (const field of fromSchema) {
      const val = getByPath(nextCustomData, field.key);
      if (val === undefined) continue;

      const isSupportedByTo = toSchema.some((targetField) => targetField.key === field.key);
      if (!isSupportedByTo) {
        // Move to parked
        newlyParked[field.key] = val;
        deleteByPath(nextCustomData, field.key);
        report.parked.push(field.label || field.key);
      } else {
        report.carriedOver.push(field.label || field.key);
      }
    }

    if (Object.keys(newlyParked).length > 0) {
      parkedStore[fromTemplateId] = newlyParked;
    }
  } else if (toSchema) {
    // Check carried over fields when target has a schema
    for (const field of toSchema) {
      const val = getByPath(nextCustomData, field.key);
      if (val !== undefined) {
        report.carriedOver.push(field.label || field.key);
      }
    }
  }

  // Step 3: Check missing fields declared by target template but absent in draft
  if (toSchema) {
    for (const field of toSchema) {
      const val = getByPath(nextCustomData, field.key);
      if (val === undefined) {
        report.missing.push(field.label || field.key);
      }
    }
  }

  // Step 4: Prune parkedStore to max 3 templates (LRU)
  const parkedKeys = Object.keys(parkedStore);
  if (parkedKeys.length > 3) {
    const keysToRemove = parkedKeys.slice(0, parkedKeys.length - 3);
    for (const k of keysToRemove) {
      delete parkedStore[k];
    }
  }

  // Re-attach _parked if non-empty
  if (Object.keys(parkedStore).length > 0) {
    nextCustomData._parked = parkedStore;
  }

  return {
    customData: nextCustomData,
    report,
  };
}
